import {
  BookOpen,
  CheckCircle,
  ChevronRight,
  Clock,
  GraduationCap,
  Pencil,
  PlayCircle,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCourse, useCourseLessons } from '@/hooks/use-courses'
import { useCreateEnrollment } from '@/hooks/use-enrollments'
import { useCourseLearnData } from '@/hooks/use-learn'
import { useCourseProgress } from '@/hooks/use-progress'
import { useAuth } from '@/lib/auth'
import { useTenantPath } from '@/lib/tenant'

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tp = useTenantPath()
  const { user } = useAuth()

  const { data: course, isLoading } = useCourse(id!)
  const { data: lessonIds } = useCourseLessons(id!)
  const { data: progressData } = useCourseProgress(id!)
  const { data: learnData } = useCourseLearnData(id!)
  const enrollMutation = useCreateEnrollment()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const c = course as any
  const lessonIdList = (lessonIds as any) ?? []
  const progress = progressData as any
  const learn = learnData as any
  const lessons = learn?.course?.lessons ?? []
  const completedLessonIds: string[] = learn?.completedLessonIds ?? []
  const enrollment = learn?.enrollment

  const isInstructorOrAdmin =
    user &&
    (c?.instructorId === user.id ||
      user.role === 'admin' ||
      user.role === 'instructor' ||
      user.role === 'superadmin')

  const handleEnroll = async () => {
    try {
      await enrollMutation.mutateAsync({ courseId: id! })
      toast.success('受講登録しました')
    } catch {
      toast.error('受講登録に失敗しました')
    }
  }

  const handleStartLearning = () => {
    navigate(tp(`/learn/${id}`))
  }

  const breadcrumbs = [
    { label: 'ホーム', href: tp('/') },
    { label: 'コース一覧', href: tp('/courses') },
    { label: c?.title ?? 'コース詳細', href: '' },
  ]

  const totalLessons = lessons.length || lessonIdList.length || 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* パンくずリスト */}
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((item, index) => (
              <div key={item.href || item.label} className="flex items-center">
                {index > 0 && <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-gray-600">{item.label}</span>
                ) : (
                  <Link to={item.href} className="text-blue-600 hover:underline">
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* ヒーローセクション */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl">
            <h1 className="mb-4 text-4xl font-bold">{c?.title}</h1>
            <p className="mb-6 text-xl opacity-90">{c?.description}</p>
            <div className="mb-8 flex flex-wrap items-center gap-6">
              {c?.category && (
                <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
                  {c.category}
                </Badge>
              )}
              {c?.level && (
                <Badge variant="outline" className="border-white/40 text-white">
                  {c.level}
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                <span>{totalLessons}レッスン</span>
              </div>
              {c?.duration && (
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>{c.duration}</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-4">
              {!enrollment && (
                <Button
                  size="lg"
                  onClick={handleEnroll}
                  disabled={enrollMutation.isPending}
                  className="bg-white text-blue-700 hover:bg-gray-100"
                >
                  <BookOpen className="mr-2 h-5 w-5" />
                  受講登録
                </Button>
              )}
              {enrollment && (
                <Button
                  size="lg"
                  onClick={handleStartLearning}
                  className="bg-white text-blue-700 hover:bg-gray-100"
                >
                  <PlayCircle className="mr-2 h-5 w-5" />
                  学習を{progress?.percentage > 0 ? '続ける' : '開始'}
                </Button>
              )}
              {isInstructorOrAdmin && (
                <Link to={tp(`/teacher/courses/${c?.id}/edit`)}>
                  <Button
                    size="lg"
                    variant="secondary"
                    className="bg-white/20 text-white hover:bg-white/30"
                  >
                    <Pencil className="mr-2 h-5 w-5" />
                    コースを編集
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 左側：コース詳細 */}
          <div className="space-y-8 lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="overview">概要</TabsTrigger>
                <TabsTrigger value="curriculum">カリキュラム</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="mb-4 text-xl font-semibold">このコースについて</h3>
                    <div className="prose max-w-none whitespace-pre-wrap">
                      {c?.markdownSummary || c?.description || '説明がありません。'}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="mb-4 text-xl font-semibold">学習内容</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 h-5 w-5 text-green-500" />
                        <span>基礎から応用まで段階的に学習</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 h-5 w-5 text-green-500" />
                        <span>実践的な演習問題</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 h-5 w-5 text-green-500" />
                        <span>修了証の発行</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="curriculum" className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="mb-6 flex items-center justify-between">
                      <h3 className="text-lg font-semibold">レッスン一覧</h3>
                      <span className="text-sm text-gray-500">{totalLessons}レッスン</span>
                    </div>
                    <div className="space-y-3">
                      {lessons.length > 0
                        ? lessons.map((lesson: any, lessonIndex: number) => (
                            <Link to={tp(`/learn/${id}`)} key={lesson.id} className="block">
                              <div className="flex items-center gap-3 rounded-lg border border-gray-100 p-4 transition-colors hover:bg-gray-50">
                                <div className="w-8 shrink-0 text-center text-sm font-medium text-gray-500">
                                  {lessonIndex + 1}
                                </div>
                                <div className="shrink-0">
                                  {lesson.type === 'video' ? (
                                    <PlayCircle className="h-5 w-5 text-blue-500" />
                                  ) : (
                                    <BookOpen className="h-5 w-5 text-gray-500" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-medium">{lesson.title}</h4>
                                  <p className="text-sm text-gray-500">
                                    {lesson.type === 'video' ? '動画' : 'テキスト'}
                                    {lesson.videoTotalDurationSeconds
                                      ? ` - ${formatDuration(lesson.videoTotalDurationSeconds)}`
                                      : ''}
                                  </p>
                                </div>
                                {completedLessonIds.includes(lesson.id) && (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                )}
                              </div>
                            </Link>
                          ))
                        : lessonIdList.map((lessonId: string, index: number) => (
                            <div
                              key={lessonId}
                              className="flex items-center gap-3 rounded-lg border border-gray-100 p-4"
                            >
                              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">
                                {index + 1}
                              </span>
                              <span className="font-medium">レッスン {index + 1}</span>
                            </div>
                          ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* 右側：サイドバー */}
          <div className="space-y-6">
            {/* 進捗カード */}
            {enrollment && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 font-semibold">あなたの進捗</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="mb-2 flex justify-between text-sm">
                        <span>完了率</span>
                        <span className="font-medium">{progress?.percentage ?? 0}%</span>
                      </div>
                      <Progress value={progress?.percentage ?? 0} />
                    </div>
                    <div className="text-sm text-gray-500">
                      {progress?.completed ?? 0} / {progress?.total ?? totalLessons} レッスン完了
                    </div>
                    <Button className="w-full" onClick={handleStartLearning}>
                      {progress?.percentage > 0 ? '学習を続ける' : '学習を開始'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 未登録者向け受講登録カード */}
            {!enrollment && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 font-semibold">このコースを受講</h3>
                  <p className="mb-4 text-sm text-gray-500">受講登録をして学習を始めましょう。</p>
                  <Button
                    className="w-full"
                    onClick={handleEnroll}
                    disabled={enrollMutation.isPending}
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    受講登録する
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* インストラクター情報 */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 font-semibold">インストラクター</h3>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
                    <GraduationCap className="h-6 w-6 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium">講師</p>
                    <p className="text-sm text-gray-500">専門講師</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* コース情報 */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 font-semibold">コース情報</h3>
                <div className="space-y-3 text-sm">
                  {c?.level && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">レベル</span>
                      <span>{c.level}</span>
                    </div>
                  )}
                  {c?.category && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">カテゴリ</span>
                      <span>{c.category}</span>
                    </div>
                  )}
                  {c?.duration && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">所要時間</span>
                      <span>{c.duration}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">レッスン数</span>
                    <span>{totalLessons}レッスン</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Format seconds to "MM:SS" or "HH:MM:SS" */
function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${m}:${String(s).padStart(2, '0')}`
}
